import { FC, Key, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Collapse, Divider, Input, Modal, Row, Space } from 'antd';
import type { TreeProps } from 'antd/es/tree';
import {
    TbArrowRight,
    TbChevronDown,
    TbCopyPlus,
    TbShieldCheck,
    TbSquareCheckFilled,
    TbUserBolt,
    TbX,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { LearnMoreFooter, MiddleDot, ModalFormTableOfContents, OverflowFadeScrollArea } from '@/shared';
import { PlatformForm as Form } from '@/shared/components/forms';
import { PreferenceModalHeader } from '@/shared/components/modals/PreferenceModalPrimitives';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useRoleStore } from '@/stores/database/entities/00-RBAC/04-Roles';
import { SetStateMethod } from '@/types';

import RolePolicyTree from '../RolePolicyTree';
import {
    getRolePermissionsForKeys,
    isRolePolicyKey,
    ROLE_POLICY_COUNT,
    ROLE_POLICY_SECTIONS,
} from '../RolePolicyTree/rolePolicies';
import RoleTemplates from '../RoleTemplates';
import { RoleTemplate } from '../RoleTemplates/roleTemplates';
import Sider from './layout/01-Sider';
import { RoleBuilderSection, roleBuilderSteps } from './steps';

interface CreateRoleModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

interface RoleFormValues {
    name: string;
}

const CreateRoleModal: FC<CreateRoleModalProps> = ({ open, setOpen }): JSX.Element => {
    const [form] = Form.useForm<RoleFormValues>();
    const [submitting, setSubmitting] = useState(false);
    const [checkedKeys, setCheckedKeys] = useState<Key[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [templatesOpen, setTemplatesOpen] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const { createRole } = useRoleStore();
    const { token } = useAntDesignTheme();

    const selectedPolicyKeys = checkedKeys.map(String).filter(isRolePolicyKey);

    useEffect(() => {
        if (!open) {
            return;
        }

        form.resetFields();
        setCheckedKeys([]);
        setSelectedTemplate('');
        setTemplatesOpen(false);
    }, [form, open]);

    const onCheck: TreeProps['onCheck'] = (checked) => {
        setCheckedKeys(Array.isArray(checked) ? checked : checked.checked);
        setSelectedTemplate('');
    };

    const applyTemplate = (template: RoleTemplate) => {
        form.setFieldValue('name', template.label);
        setCheckedKeys(template.policyKeys);
        setSelectedTemplate(template.key);
    };

    const navigateToSection = (section: RoleBuilderSection) => {
        if (section === 'templates') {
            setTemplatesOpen(true);
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const sectionElement = scrollContainerRef.current?.querySelector<HTMLElement>(
                    `#create-role-${section}`,
                );

                sectionElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        });
    };

    const handleCreateRole = async () => {
        try {
            const values = await form.validateFields();
            const permissions = getRolePermissionsForKeys(selectedPolicyKeys);

            if (permissions.length === 0) {
                toast.error('Select at least one custom policy');
                return;
            }

            setSubmitting(true);

            const role = await createRole({
                name: values.name.trim(),
                permissions,
            });

            if (!role) {
                toast.error('Failed to create role');
                return;
            }

            toast('Role Created', {
                icon: <TbUserBolt size='1rem' />,
                description: role.name,
            });

            setOpen(false);
        } catch (error) {
            if (error && typeof error === 'object' && 'errorFields' in error) {
                return;
            }

            captureException(error);
            toast.error('Failed to create role');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            destroyOnHidden
            open={open}
            mask={false}
            width='min(88rem, calc(100vw - 4rem))'
            closable={false}
            closeIcon={null}
            styles={{
                body: { height: '54rem', maxHeight: 'calc(100vh - 6rem)', overflow: 'hidden' },
                content: { padding: 0, margin: 0, border: `1px solid ${token.colorBorder}` },
            }}
            footer={null}
            onCancel={() => setOpen(false)}
        >
            <Row wrap={false} style={{ height: '100%' }}>
                <Col span={7} style={{ height: '100%' }}>
                    <Sider />
                </Col>
                <Col
                    flex='auto'
                    style={{
                        minWidth: 0,
                        height: '100%',
                        borderLeft: `1px solid ${token.colorBorder}`,
                    }}
                >
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <PreferenceModalHeader
                            description='Define a governed role identity, permissions, and optional template baseline.'
                            title='Create Role'
                            titleLevel={4}
                            extra={
                                <Button
                                    aria-label='Close create role modal'
                                    icon={<TbX style={{ color: token.colorTextTertiary }} />}
                                    size='small'
                                    type='text'
                                    onClick={() => setOpen(false)}
                                />
                            }
                        />
                        <Form
                            form={form}
                            layout='vertical'
                            requiredMark={false}
                            style={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: 'column', padding: '1rem' }}
                        >
                            <div
                                style={{
                                    border: `1px dashed ${token.colorBorder}`,
                                    borderRadius: token.borderRadiusLG,
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                <Row wrap={false} style={{ height: '100%' }}>
                                    <Col flex='auto' style={{ minHeight: 0, minWidth: 0 }}>
                                        <OverflowFadeScrollArea
                                            aria-label='Role configuration'
                                            ref={scrollContainerRef}
                                            containerStyle={{ height: '100%', minHeight: 0 }}
                                            style={{ padding: '1rem' }}
                                            surface='elevated'
                                        >
                                            <section id='create-role-details' style={{ scrollMarginTop: '1rem' }}>
                                                <Form.Item
                                                    name='name'
                                                    style={{ marginBottom: '0.5rem' }}
                                                    label='Role Name'
                                                    rules={[
                                                        {
                                                            required: true,
                                                            whitespace: true,
                                                            message: 'Enter a role name',
                                                        },
                                                    ]}
                                                >
                                                    <Input
                                                        prefix={
                                                            <TbShieldCheck
                                                                size='1rem'
                                                                style={{
                                                                    marginRight: '0.375rem',
                                                                    color: token.colorTextQuaternary,
                                                                }}
                                                            />
                                                        }
                                                        placeholder='Portfolio Manager...'
                                                    />
                                                </Form.Item>
                                            </section>

                                            <section id='create-role-policies' style={{ scrollMarginTop: '1rem' }}>
                                                <Divider
                                                    orientation='left'
                                                    style={{ marginTop: 0, marginBottom: '0.25rem' }}
                                                >
                                                    <small style={{ color: token.colorTextTertiary }}>
                                                        Custom Policies
                                                    </small>
                                                </Divider>

                                                <Row justify='space-between' style={{ marginBottom: '0.5rem' }}>
                                                    <Col>
                                                        <small style={{ color: token.colorTextTertiary }}>
                                                            {ROLE_POLICY_SECTIONS.length} Sections
                                                            <MiddleDot horizontalMargin='0.5rem' />
                                                            {ROLE_POLICY_COUNT} Policies
                                                        </small>
                                                    </Col>
                                                    <Col>
                                                        <Space.Compact>
                                                            <Button size='small' style={{ pointerEvents: 'none' }}>
                                                                <TbSquareCheckFilled
                                                                    style={{
                                                                        verticalAlign: '-0.125rem',
                                                                        marginRight: '0.325rem',
                                                                        color: token.colorTextTertiary,
                                                                    }}
                                                                />
                                                                {selectedPolicyKeys.length} Selected
                                                            </Button>
                                                            <Button
                                                                aria-label='Clear selected role policies'
                                                                size='small'
                                                                type='dashed'
                                                                icon={
                                                                    <TbX style={{ color: token.colorTextTertiary }} />
                                                                }
                                                                onClick={() => {
                                                                    setCheckedKeys([]);
                                                                    setSelectedTemplate('');
                                                                }}
                                                            />
                                                        </Space.Compact>
                                                    </Col>
                                                </Row>

                                                <RolePolicyTree checkedKeys={checkedKeys} onCheck={onCheck} />
                                            </section>

                                            <section id='create-role-templates' style={{ scrollMarginTop: '1rem' }}>
                                                <Collapse
                                                    size='small'
                                                    activeKey={templatesOpen ? ['templates'] : []}
                                                    expandIconPosition='end'
                                                    expandIcon={({ isActive }) => (
                                                        <TbChevronDown
                                                            style={{
                                                                color: token.colorTextTertiary,
                                                                transform: isActive ? undefined : 'rotate(-90deg)',
                                                            }}
                                                        />
                                                    )}
                                                    style={{
                                                        marginTop: '0.75rem',
                                                        border: `1px dashed ${token.colorBorder}`,
                                                        borderRadius: token.borderRadius,
                                                        backgroundColor: token.colorBgContainer,
                                                    }}
                                                    items={[
                                                        {
                                                            key: 'templates',
                                                            label: (
                                                                <span
                                                                    style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: '0.5rem',
                                                                    }}
                                                                >
                                                                    <TbCopyPlus
                                                                        style={{ color: token.colorTextTertiary }}
                                                                    />
                                                                    Templates
                                                                </span>
                                                            ),
                                                            children: (
                                                                <RoleTemplates
                                                                    selectedTemplate={selectedTemplate}
                                                                    onSelect={applyTemplate}
                                                                />
                                                            ),
                                                        },
                                                    ]}
                                                    onChange={(keys) => {
                                                        const activeKeys = Array.isArray(keys) ? keys : [keys];
                                                        const nextOpen = activeKeys.includes('templates');

                                                        setTemplatesOpen(nextOpen);
                                                    }}
                                                />
                                            </section>
                                            <Row
                                                align='middle'
                                                gutter={[16, 12]}
                                                style={{
                                                    borderTop: `1px solid ${token.colorBorder}`,
                                                    margin: '1rem -1rem -1rem',
                                                    padding: '1rem',
                                                }}
                                            >
                                                <Col flex='auto'>
                                                    <LearnMoreFooter align='left' topic='Custom Roles' />
                                                </Col>
                                                <Col flex='none'>
                                                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                                                    <Button
                                                        disabled={selectedPolicyKeys.length === 0}
                                                        loading={submitting}
                                                        type='primary'
                                                        style={{ marginLeft: 8 }}
                                                        onClick={handleCreateRole}
                                                    >
                                                        Create Role
                                                        <TbArrowRight size='1rem' />
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </OverflowFadeScrollArea>
                                    </Col>
                                    <Col
                                        flex='15rem'
                                        style={{
                                            backgroundColor: token.colorBgBase,
                                            borderLeft: `1px solid ${token.colorBorderSecondary}`,
                                            padding: '1rem',
                                        }}
                                    >
                                        <ModalFormTableOfContents
                                            items={roleBuilderSteps.map((step) => {
                                                const StepIcon = step.icon;

                                                return { icon: <StepIcon />, key: step.key, label: step.label };
                                            })}
                                            onSelect={navigateToSection}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </Form>
                    </div>
                </Col>
            </Row>
        </Modal>
    );
};

export default CreateRoleModal;
